import React, { useState, useEffect, useMemo } from 'react';
import {
    StyleSheet,
    Text,
    View,
    TextInput,
    TouchableOpacity
} from 'react-native';
import moment from 'moment';
import { AntDesign, Ionicons } from '@expo/vector-icons';
import { useIsFocused } from '@react-navigation/native';
import DataTable from "react-data-table-component";
import Modal from "react-native-modal";
import { sensorState, RssiCell, LastSeenCell, useScale, ScaleFn, TablePagination , useDarkMode} from './common';
import Data from './Data';
import { getStorage, setStorage } from '../storage';

// ─── 테마 토큰 ────────────────────────────────────────────
const DARK = {
    bg:        '#0F172A',
    card:      '#1E293B',
    cardInner: '#0F172A',
    border:    'rgba(99,179,237,0.12)',
    text:      '#E2E8F0',
    textSub:   '#94A3B8',
    muted:     '#a8adb497',
    trackBg:   'rgba(255,255,255,0.05)',
};
const LIGHT = {
    bg:        '#F9FAFB',
    card:      '#FFFFFF',
    cardInner: '#F3F4F6',
    border:    '#E5E7EB',
    text:      '#111827',
    textSub:   '#4B5563',
    muted:     '#9CA3AF',
    trackBg:   'rgba(0,0,0,0.03)',
};
type Theme = typeof DARK;

const S = {
    accent:  '#38BDF8',
    ok:      '#34D399',
    err:     '#F87171',
    warn:    '#FB923C',
    replace: '#FBBF24',
    purple:  '#A78BFA',
    pink:    '#F472B6',
};

// ─── 모달 (테마 적용) ─────────────────────────────────────
const OpenModal = ({ modalVisible, setModalVisible, T }: any) => {
    const { scale, s } = useScale();
    const styles = useMemo(() => makeStyles(s), [scale]);
    const [text, setText] = useState('');
    const [id, setID] = useState('');

    const onChangeText = (text: any) => { setText(text) };
    const onChangeRoom = (text: any) => { setID(text) };

    return (
        <Modal
            style={{ flex: 1 }}
            backdropColor="black"
            isVisible={modalVisible}
            backdropOpacity={0.7}
            onBackdropPress={() => { setModalVisible(false); }}
        >
            <View style={[styles.modalView, { backgroundColor: T.card, borderColor: T.border }]}>
                <View style={[styles.modalHeader, { borderColor: T.border }]}>
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                        <Text style={[styles.modalTitle, { color: T.text }]}>센서 등록</Text>
                    </View>
                    <TouchableOpacity onPress={() => setModalVisible(false)}>
                        <AntDesign name='close' size={s(28)} color={T.textSub} />
                    </TouchableOpacity>
                </View>
                <View style={styles.box}>
                    <View style={styles.rowstyle}>
                        <Text style={[styles.contentFont, { color: T.text }]}>중계기 ID</Text>
                        <View style={[styles.textInputBox, { borderColor: T.border, backgroundColor: T.cardInner }]}>
                            <TextInput autoCapitalize='none' clearButtonMode='always'
                                style={[styles.textInput, { color: T.text }]}
                                onChangeText={(text: any) => { onChangeRoom(text) }}
                                value={id}
                                placeholderTextColor={T.muted} />
                        </View>
                    </View>
                    <View style={styles.rowstyle}>
                        <Text style={[styles.contentFont, { color: T.text }]}>시리얼 넘버</Text>
                        <View style={[styles.textInputBox, { borderColor: T.border, backgroundColor: T.cardInner }]}>
                            <TextInput autoCapitalize='none' clearButtonMode='always'
                                style={[styles.textInput, { color: T.text }]}
                                onChangeText={(text: any) => { onChangeText(text) }}
                                value={text}
                                placeholderTextColor={T.muted} />
                        </View>
                    </View>
                </View>
                <View style={styles.buttonBox}>
                    <TouchableOpacity style={styles.modifyButton}
                        onPress={() => { alert('등록되었습니다.'); setModalVisible(!modalVisible); }} >
                        <Text style={styles.buttonFont}>등록</Text>
                    </TouchableOpacity>
                    <TouchableOpacity style={[styles.modifyButton, { backgroundColor: "#1890FF", }]}
                        onPress={() => { alert('취소되었습니다.'); setModalVisible(!modalVisible) }} >
                        <Text style={styles.buttonFont}>취소</Text>
                    </TouchableOpacity>
                </View>
            </View>
        </Modal>
    )
};

// ─── 테이블 컬럼 ──────────────────────────────────────────
const sensorColumns = [
    {
        id: 1,
        name: "no.",
        selector: (row: any, index: any) => index + 1,
        grow: 0
    },
    {
        id: 2,
        name: "이름",
        selector: (row: any) => row.NAME,
        sortable: true,
        sortField: 'NAME',
    },
    {
        id: 3,
        name: '성별',
        selector: (row: any) => row.GENDER,
        sortable: true,
        sortField: 'GENDER',
    },
    {
        id: 4,
        name: '생년월일',
        selector: (row: any) => row.BIRTH ? moment(row.BIRTH).format('YYYY-MM-DD') : '',
        sortable: true,
        sortField: 'BIRTH',
    },
    {
        id: 5,
        name: '병실 호수',
        selector: (row: any) => row.ROOM_NO,
        sortable: true,
        sortField: 'ROOM_NO',
    },
    {
        id: 6,
        name: "침대 no.",
        selector: (row: any) => row.BED_NO ? row.BED_NO : '',
        sortable: true,
        sortField: 'BED_NO',
    },
    {
        id: 7,
        name: '센서 ID',
        selector: (row: any) => row.SENSOR_ID,
        sortable: true,
        sortField: 'SENSOR_ID',
    },
    {
        id: 8,
        name: '연결 상태',
        selector: (row: any) => sensorState(row),
        sortable: true,
        sortField: 'CONNECT',
    },
    {
        id: 10,
        name: '마지막 수신',
        selector: (row: any) => LastSeenCell(row),
        sortable: true,
        sortField: 'LAST_MIN',
        minWidth: '120px',
        wrap: true,
    },
    {
        id: 9,
        name: '신호',
        selector: (row: any) => RssiCell(row),
        grow: 0,
        sortable: true,
        sortField: 'RSSI',
    },
];

// ─── 메인 컴포넌트 ────────────────────────────────────────
export default function Sensor({ navigation }: any) {
    const isFocused = useIsFocused();
    const { scale, s } = useScale();
    const styles = useMemo(() => makeStyles(s), [scale]);
    const [modalVisible, setModalVisible] = useState(false);
    const [list, setList] = useState<any[]>([]);
    const [filteredList, setFilteredList] = useState<any>([]);
    const [sortField, setSortField] = useState<string | null>(null);
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const _totalPages = Math.max(1, Math.ceil(((filteredList) || []).length / perPage));
    const _curPage = Math.min(Math.max(1, page), _totalPages);
    // 헤더 클릭 정렬 — 전체 목록(filteredList) 기준으로 정렬 후 페이지 slice
    const sortedList = useMemo(() => {
        const arr = [...((filteredList) || [])];
        if (!sortField) return arr;
        const numeric = sortField === 'RSSI' || sortField === 'CONNECT' || sortField === 'LAST_MIN';
        arr.sort((a: any, b: any) => {
            let av = a?.[sortField], bv = b?.[sortField];
            if (numeric) {
                av = (av === null || av === undefined || av === '') ? -Infinity : Number(av);
                bv = (bv === null || bv === undefined || bv === '') ? -Infinity : Number(bv);
                return sortDir === 'asc' ? av - bv : bv - av;
            }
            av = (av === null || av === undefined) ? '' : String(av);
            bv = (bv === null || bv === undefined) ? '' : String(bv);
            return sortDir === 'asc' ? av.localeCompare(bv, 'ko') : bv.localeCompare(av, 'ko');
        });
        return arr;
    }, [filteredList, sortField, sortDir]);
    const pageData = ((sortedList) || []).slice((_curPage - 1) * perPage, _curPage * perPage);
    const [searchText, setSearchText] = useState('');
    const { dark, setDark } = useDarkMode();
    const [admin, setAdmin] = useState('1');

    // 다크모드 로드
    useEffect(() => {
        if (!isFocused) return;
        getStorage('darkMode').then((v: any) => { if (v !== null) setDark(v); });
    }, [isFocused]);

    const T = dark ? DARK : LIGHT;

    // 테이블 스타일 (테마 연동) — 행 구분선 조금 진하게
    const dvd = dark ? 'rgba(148,163,184,0.30)' : '#CBD5E1';
    const tableStyles = {
        headRow:    { style: { backgroundColor: T.cardInner, borderBottomColor: dvd, borderBottomWidth: 1, minHeight: s(40) } },
        headCells:  { style: { color: T.textSub, fontSize: s(12), fontWeight: '600' as any, paddingLeft: s(12), paddingRight: s(12) } },
        rows:       { style: { backgroundColor: T.card, color: T.text, borderBottomColor: dvd, borderBottomWidth: 1, minHeight: s(44), '&:hover': { backgroundColor: T.cardInner } } },
        cells:      { style: { color: T.text, fontSize: s(13), paddingLeft: s(12), paddingRight: s(12) } },
        pagination: { style: { backgroundColor: T.card, color: T.textSub, borderTopColor: T.border, borderTopWidth: 1, minHeight: s(44) },
            pageButtonsStyle: { color: T.textSub, fill: T.textSub, '&:disabled': { color: T.muted, fill: T.muted }, '&:hover:not(:disabled)': { backgroundColor: T.cardInner } } },
        noData:     { style: { backgroundColor: T.card, color: T.muted, padding: s(30) } },
    };

    // 데이터 로드 (ADMIN 분기)
    useEffect(() => {
        const load = async () => {
            const userInfo = await getStorage('AutoLogin');
            const adminValue = userInfo?.admin ?? '1';
            setAdmin(adminValue);

            const endpoint = adminValue === '0'
                ? 'sensor/getSensorAllList'
                : 'sensor/getSensorAllListbyUser';

            Data.fetchData(endpoint, {}, (result: any, count: any) => {
                if (count > 0) {
                    const sortedList = result.sort((a: any, b: any) => {
                        if (a.AREA === '로밍') return 1;
                        if (b.AREA === '로밍') return -1;
                        return a.AREA.localeCompare(b.AREA, 'ko');
                    });
                    setList(sortedList);
                } else {
                    setList([]);
                }
            }, true);
        };

        if (!isFocused) return;
        load();
    }, [isFocused]);

    // 검색 필터링 로직
    useEffect(() => {
        if (!list) return;
        if (searchText === '') {
            setFilteredList(list);
        } else {
            const filtered = list.filter((item: any) =>
                item?.NAME?.includes(searchText) || item?.ROOM_NO?.includes(searchText)
            );
            setFilteredList(filtered);
        }
    }, [searchText, list]);

    return (
        <View style={[styles.container, { backgroundColor: T.bg }]}>
            <View style={[styles.dataTable, { backgroundColor: T.card, borderColor: T.border, shadowColor: dark ? 'transparent' : '#d9dbec' }]}>
                {/* ─── 헤더 ─── */}
                <View style={styles.TopBox}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(10) }}>
                        <Text style={{ fontSize: s(18), fontWeight: '700', color: T.text }}>센서 목록</Text>
                        {filteredList?.length > 0 && (
                            <View style={{ backgroundColor: S.accent + '18', paddingHorizontal: s(8), paddingVertical: s(2), borderRadius: s(10) }}>
                                <Text style={{ fontSize: s(12), fontWeight: '700', color: S.accent }}>{filteredList.length}</Text>
                            </View>
                        )}
                    </View>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(10) }}>
                        <View style={[styles.searchContainer, { backgroundColor: T.cardInner, borderColor: T.border }]}>
                            <Ionicons name="search-sharp" size={s(18)} color={T.muted} style={{ marginRight: s(8) }} />
                            <TextInput
                                style={[styles.searchBox, { color: T.text }]}
                                placeholder="이름, 병실 검색..."
                                placeholderTextColor={T.muted}
                                value={searchText}
                                onChangeText={(text) => setSearchText(text)}
                            />
                            {searchText !== '' && (
                                <TouchableOpacity onPress={() => setSearchText('')} style={{ padding: s(2) }}>
                                    <AntDesign name="closecircle" size={s(14)} color={T.muted} />
                                </TouchableOpacity>
                            )}
                        </View>
                        <TouchableOpacity
                            style={[styles.iconBtn, { backgroundColor: dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)' }]}
                            onPress={() => { navigation.navigate('Setting') }}
                        >
                            <Ionicons name="settings-sharp" size={s(16)} color={T.textSub} />
                        </TouchableOpacity>
                    </View>
                </View>
                {/* ─── 테이블 ─── */}
                <DataTable
                    columns={sensorColumns}
                    data={pageData}
                    sortServer
                    onSort={(column: any, direction: any) => {
                        setSortField(column?.sortField ?? null);
                        setSortDir(direction === 'desc' ? 'desc' : 'asc');
                        setPage(1);
                    }}
                    customStyles={tableStyles}
                    noDataComponent={
                        <View style={{ padding: s(40), alignItems: 'center', justifyContent: 'center' }}>
                            <Text style={{ fontSize: s(16), color: T.muted }}>
                                등록된 센서가 없습니다.
                            </Text>
                        </View>
                    }
                    onRowClicked={(row: any) => {
                        try { window.localStorage.setItem(`_navParams_SensorDetail_${row.SENSOR_ID}`, JSON.stringify({ data: row })); } catch {}
                        navigation.navigate('SensorDetail', { id: String(row.SENSOR_ID) });
                    }}
                />
                {(filteredList || []).length > 0 && (
                    <TablePagination
                        rowCount={(filteredList || []).length}
                        perPage={perPage}
                        page={_curPage}
                        onChangePage={setPage}
                        onChangePerPage={(n) => { setPerPage(n); setPage(1); }}
                        T={T}
                        s={s}
                        accent={S.accent}
                    />
                )}
            </View>
            <OpenModal modalVisible={modalVisible} setModalVisible={setModalVisible} T={T} />
        </View>
    );
}

// ─── 스타일 ───────────────────────────────────────────────
const makeStyles = (s: ScaleFn) => StyleSheet.create({
    container: {
        flex: 1,
        padding: s(24),
    },
    dataTable: {
        borderRadius: s(16),
        borderWidth: 0.5,
        overflow: 'hidden',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.08,
        shadowRadius: s(20),
        elevation: 3,
        paddingTop: s(20),
        paddingHorizontal: s(10),
    },
    TopBox: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: s(6),
        paddingBottom: s(12),
    },
    searchContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        height: s(36),
        borderWidth: 1,
        borderRadius: s(10),
        paddingHorizontal: s(12),
        width: s(240),
    },
    searchBox: {
        flex: 1,
        fontSize: s(14),
        outlineStyle: 'none',
    },
    iconBtn: {
        width: s(34),
        height: s(34),
        borderRadius: s(10),
        justifyContent: 'center',
        alignItems: 'center',
    },
    buttonBox: {
        marginTop: s(20),
        flexDirection: 'row',
        alignItems: 'center',
        alignSelf: 'center',
    },
    modifyButton: {
        paddingHorizontal: s(20),
        paddingVertical: s(6),
        justifyContent: 'center',
        alignItems: 'center',
        borderWidth: 0,
        borderRadius: s(10),
        backgroundColor: '#FF8A80',
        marginRight: s(20),
    },
    buttonFont: {
        fontSize: s(16),
        fontWeight: '600',
        color: '#fff',
    },
    modalView: {
        margin: s(20),
        borderWidth: 1,
        borderRadius: s(20),
        padding: s(35),
        alignSelf: 'center',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.25,
        shadowRadius: s(4),
        elevation: 5,
        width: '60%',
    },
    modalHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingBottom: s(10),
        marginBottom: s(10),
        borderBottomWidth: 3,
    },
    modalTitle: {
        fontSize: s(22),
        fontWeight: '700',
        textAlign: 'center',
        marginRight: s(20),
    },
    box: {
        width: '100%',
        borderWidth: 0,
        justifyContent: 'center',
        alignItems: 'center',
        paddingTop: s(10),
    },
    rowstyle: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: s(5),
        width: '70%',
        borderWidth: 0,
        justifyContent: 'space-between',
    },
    contentFont: {
        fontSize: s(16),
        fontWeight: '600',
    },
    textInputBox: {
        borderWidth: 1,
        width: '50%',
        height: s(30),
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: s(6),
    },
    textInput: {
        fontSize: s(18),
        width: '100%',
        height: '100%',
        paddingHorizontal: s(10),
    },
});
